/**
 * ActivityBridge — the seam between the client and the host surface. Ported from Ringmaster's
 * `discord/ActivityBridge.ts` with the DESIGN-client §13 extensions (layout mode, orientation,
 * hardware acceleration, haptics) and a third implementation for the plain web.
 *
 *   DiscordActivityBridge — wraps @discord/embedded-app-sdk (inside the Activity iframe).
 *   MockActivityBridge    — fabricated instance/user/voice from `?mock=1` query params.
 *   WebBridge             — plain browser: guest identity, no voice, no invite dialog.
 */

export type ActivityPlatform = 'desktop' | 'mobile';
export type BridgeKind = 'discord' | 'mock' | 'web';
export type LayoutMode = 'focused' | 'pip' | 'grid';
export type Orientation = 'portrait' | 'landscape';

export interface ActivityParticipant {
  id: string;
  username: string;
  /** Best display name (global name → username). */
  displayName: string;
  avatarUrl: string | null;
}

/**
 * One voice-state change for one user (SPEAKING_START/STOP → `speaking`, VOICE_STATE_UPDATE →
 * `muted`). A single update carries one or both fields.
 */
export interface VoiceUpdate {
  userId: string;
  speaking?: boolean;
  muted?: boolean;
}

export interface ActivityBridge {
  readonly kind: BridgeKind;

  /** Handshake with the host. Must resolve before instanceId/channelId/platform are read. */
  ready(): Promise<void>;

  /**
   * Request an OAuth2 authorization code. Discord: a scope ladder, widest first —
   * `['identify','email','rpc.voice.read']`, `['identify','email']`, `['identify','rpc.voice.read']`,
   * `['identify']` — so a declined optional scope costs a rung and never the boot.
   * `prompt:'none'` after first consent. Mock/web: a marker code the identity flow never sends
   * to Discord.
   */
  authorize(): Promise<{ code: string }>;

  /** Hand the host the server-exchanged access token (Discord RPC). No-op elsewhere. */
  authenticate(accessToken: string): Promise<void>;

  /** Activity instance id (one table per instance). Web: a per-tab id. */
  instanceId(): string;
  channelId(): string | null;
  guildId(): string | null;
  platform(): ActivityPlatform;

  /** Fires at least once with the current roster. Returns unsubscribe. */
  subscribeParticipants(cb: (participants: ActivityParticipant[]) => void): () => void;

  /**
   * True when live voice state is obtainable (`rpc.voice.read` granted AND a voice channel).
   * False → talking glow / mute icons stay off; the game is otherwise unaffected.
   */
  voiceAvailable(): boolean;

  /** Per-user speaking/mute updates; never emits when `voiceAvailable()` is false. */
  subscribeVoice(cb: (u: VoiceUpdate) => void): () => void;

  /** Whether `openInvite` has any chance (false in DMs / on the web). */
  inviteAvailable(): boolean;
  /** Open the host's invite dialog. Rejects on failure; callers fall back to the table code. */
  openInvite(): Promise<void>;

  /**
   * Open a URL outside the host. Discord: `openExternalLink` (the only way out of the iframe,
   * shows Discord's own confirmation). Web/mock: `window.open`.
   */
  openExternalLink(url: string): Promise<void>;

  /** ACTIVITY_LAYOUT_MODE_UPDATE (focused | pip | grid). Fires once with the current mode. */
  subscribeLayoutMode(cb: (m: LayoutMode) => void): () => void;
  /** ORIENTATION_UPDATE. Fires once with the current orientation. */
  subscribeOrientation(cb: (o: Orientation) => void): () => void;
  /** Lock portrait in focused mode on mobile (PIP/grid stay unlocked). No-op elsewhere. */
  lockOrientation(): Promise<void>;
  /** Desktop, once on the Boot tap. No-op elsewhere. */
  encourageHardwareAcceleration(): Promise<void>;
  /** Optional haptic tick (`navigator.vibrate` on Android web). */
  haptic?(kind: 'light' | 'medium'): void;

  /**
   * A mock bridge exposes its fabricated user so the identity flow can label the guest
   * session; real bridges return null.
   */
  mockUser(): { id: string; username: string } | null;
}

/** Shared helper: read `(pointer: coarse)` → 'mobile'. Safe when matchMedia is missing. */
export function detectPlatform(win: { matchMedia?: (q: string) => { matches: boolean } } | undefined = globalThis.window): ActivityPlatform {
  try {
    return win?.matchMedia?.('(pointer: coarse)').matches ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
}

/** Shared helper: current orientation from the viewport. */
export function detectOrientation(
  win: { innerWidth: number; innerHeight: number } | undefined = globalThis.window,
): Orientation {
  if (!win) return 'landscape';
  return win.innerHeight > win.innerWidth ? 'portrait' : 'landscape';
}

/** Subscribe to viewport-derived orientation changes (resize + orientationchange). */
export function watchOrientation(cb: (o: Orientation) => void, win: Window | undefined = globalThis.window): () => void {
  if (!win) {
    cb('landscape');
    return () => {};
  }
  let last = detectOrientation(win);
  cb(last);
  const onResize = () => {
    const now = detectOrientation(win);
    if (now !== last) {
      last = now;
      cb(now);
    }
  };
  win.addEventListener('resize', onResize);
  win.addEventListener('orientationchange', onResize);
  return () => {
    win.removeEventListener('resize', onResize);
    win.removeEventListener('orientationchange', onResize);
  };
}

export function vibrate(kind: 'light' | 'medium', nav: { vibrate?: (p: number | number[]) => boolean } | undefined = globalThis.navigator): void {
  try {
    nav?.vibrate?.(kind === 'light' ? 8 : 20);
  } catch {
    /* unsupported */
  }
}
